import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import FeedbackWidget from "@/components/dash/FeedbackWidget";
import { dashApi, type SourceMeta } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";
import {
  BarList,
  EmptyState,
  ErrorState,
  GapList,
  KpiCard,
  Leaderboard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

function SourceRight({ meta }: { meta?: SourceMeta }) {
  if (!meta?.available) return null;
  return (
    <span className="flex items-center gap-2">
      <StatusPill status={meta.status ?? "unknown"} />
      <span className="text-xs text-slate-500">{fmtRelative(meta.captured_at)}</span>
    </span>
  );
}

export default function SupportOpsPage() {
  const q = useQuery({ queryKey: ["support-operations"], queryFn: dashApi.supportOperations });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support Operations"
        description="IronChat queue, SLA, automation, channel and voice health. No message content is ever read — metadata only."
        right={<SourceRight meta={d?.source} />}
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : !d || !d.source.available ? (
        <EmptyState>No IronChat snapshot has been captured yet.</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="Unassigned now" value={d.queue?.unassigned ?? null} unit="count" tone={d.queue?.unassigned ? "danger" : undefined} />
            <KpiCard label="Awaiting reply" value={d.queue?.waiting_reply ?? null} unit="count" />
            <KpiCard label="Breaching SLA now" value={d.headline.sla_breached_now} unit="count" tone={d.headline.sla_breached_now ? "danger" : undefined} />
            <KpiCard label="SLA at risk now" value={d.sla?.at_risk_now ?? null} unit="count" />
            <KpiCard label="Missed calls unacked" value={d.voice?.missed_unacknowledged ?? null} unit="count" tone={d.voice?.missed_unacknowledged ? "danger" : undefined} />
            <KpiCard label="Webchat active now" value={d.webchat?.active_now ?? null} unit="count" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel
              title="Queue state (live conversations)"
              subtitle={d.queue ? `Oldest unassigned waiting ${d.queue.unassigned_oldest_hours}h · ${d.queue.snoozed} snoozed · ${d.queue.archived_live} archived (treated as inactive pending owner confirmation)` : undefined}
            >
              <BarList rows={d.queue?.states_live ?? []} unit="count" empty="No live conversations." />
            </Panel>
            <Panel
              title="SLA coverage & warnings"
              subtitle={d.sla ? `${d.sla.policies_enabled} enabled polic(ies) · ${d.sla.policy_coverage_pct ?? "—"}% of live queue covered` : undefined}
            >
              {d.sla ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Warnings in range</dt><dd className="font-medium text-slate-200">{d.sla.warnings_in_range}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">At risk now</dt><dd className="font-medium text-slate-200">{d.sla.at_risk_now}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Covered / total live</dt><dd className="font-medium text-slate-200">{d.sla.policy_covered} / {d.sla.policy_total}</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel
              title="Automation health"
              subtitle={d.automation ? `${d.automation.rules_enabled} of ${d.automation.rules_total} rules enabled · last run ${d.automation.last_run_at ? fmtRelative(d.automation.last_run_at) : "never"}` : undefined}
            >
              {d.automation && d.automation.run_errors > 0 && (
                <p className="mb-2 rounded bg-rose-500/10 px-2 py-1 text-xs text-rose-300">{d.automation.run_errors} run(s) errored in range.</p>
              )}
              <BarList rows={d.automation?.runs_by_status ?? []} unit="count" empty="No automation runs in range ('skipped' is normal — conditions not matched)." />
            </Panel>
            <Panel
              title="Channel & integration health"
              subtitle={d.channels ? `${d.channels.inbound_errors} inbound processing error(s) · outbox: ${d.channels.outbox_failed} failed / ${d.channels.outbox_stuck} stuck` : undefined}
            >
              {d.channels ? (
                <ul className="space-y-2 text-sm">
                  {d.channels.by_kind.map((c) => (
                    <li key={c.label} className="flex items-center justify-between">
                      <span className="text-slate-300">{c.label}</span>
                      <span className={c.probe_failed ? "text-rose-300" : "text-slate-200"}>
                        {c.value} account(s){c.probe_failed ? ` · ${c.probe_failed} probe-failed` : ""}
                      </span>
                    </li>
                  ))}
                  {!d.channels.by_kind.length && <EmptyState compact>No live channel accounts.</EmptyState>}
                </ul>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Panel title="Webchat funnel (range)">
              {d.webchat ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-400">Sessions</dt><dd className="font-medium text-slate-200">{d.webchat.sessions}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">→ Conversations</dt><dd className="font-medium text-slate-200">{d.webchat.converted} ({d.webchat.conversion_pct ?? "—"}%)</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-400">Identified visitors</dt><dd className="font-medium text-slate-200">{d.webchat.identified}</dd></div>
                </dl>
              ) : (
                <EmptyState compact>Not available in this snapshot.</EmptyState>
              )}
            </Panel>
            <Panel title="Agent load (open now)" subtitle="Current open conversations per agent — distinct from throughput.">
              {d.agent_load.length ? <Leaderboard rows={d.agent_load} unit="count" /> : <EmptyState compact>No assigned live conversations.</EmptyState>}
            </Panel>
            <Panel title="Customer 360 link coverage" subtitle={d.c360 ? `${d.c360.links_total} link(s) across ${d.c360.customers_total} customer(s)` : undefined}>
              <BarList rows={d.c360?.links_by_source ?? []} unit="count" empty="No external-id links yet." />
            </Panel>
          </div>

          <Panel
            title="Voice (range)"
            subtitle="Missed-call recovery is a direct revenue signal."
            right={<Link to="/voice/calls" className="text-xs text-brand-300 hover:text-brand-200">Call log & transcripts →</Link>}
          >
            {d.voice ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <KpiCard label="Calls" value={d.voice.calls_in_range} unit="count" />
                <KpiCard label="Missed" value={d.voice.missed_in_range} unit="count" />
                <KpiCard label="Missed unacknowledged" value={d.voice.missed_unacknowledged} unit="count" tone={d.voice.missed_unacknowledged ? "danger" : undefined} />
                <KpiCard label="Follow-ups overdue" value={d.voice.followups_overdue} unit="count" tone={d.voice.followups_overdue ? "danger" : undefined} />
                <KpiCard label="Outbound" value={d.voice.outbound_in_range ?? null} unit="count" />
                <KpiCard label="Voicemails" value={d.voice.voicemail_in_range ?? null} unit="count" />
                <KpiCard label="Transcribed" value={d.voice.transcribed_in_range ?? null} unit="count" />
                <KpiCard label="Avg duration (s)" value={d.voice.avg_duration_seconds ?? null} unit="count" />
              </div>
            ) : (
              <EmptyState compact>Not available in this snapshot.</EmptyState>
            )}
          </Panel>

          <GapList gaps={d.source.gaps} />
          <FeedbackWidget targetType="page" targetRef="/support/operations" />
        </>
      )}
    </div>
  );
}
